import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetch_cb_country_map_by_id,
  fetch_cb_cluster_map_by_id,
  fetch_cb_area_map_by_id,
  fetch_cb_thana_map_by_id,
  fetch_partner_update_data,
  fetch_partner_transaction_summary_data,
  fetch_partners,
  fetch_partners_with_transaction
} from './actions';
import {
  forward_fetch_by_selected_state,
  backward_fetch_by_selected_state
} from '../actions';
import { getCkuMapData } from './reducers';
import {
  getCbCountryDetails,
  getCbClusterDetails,
  getCbAreaDetails,
  getCbThanaDetails,
  getSelectedState,
  getFilteringState,
  getFilterType,
  getFilteringDateRange,
  COUNTRY,
  CLUSTER,
  AREA,
  THANA
} from '../reducers';
import {
  make_choropleth,
  make_boundary,
  make_label_by_state,
  make_legends_by_state,
  make_summary_by_state,
  make_markers_by_state,
  merge_regional_data,
  merge_regional_marker_data
} from './utils';
import Map from '../../../Map';

export default function(props) {
  const [choropleth, setChoropleth] = useState(null);
  const [boundary, setBoundary] = useState(null);
  const [label, setLabel] = useState(null);
  const [legends, setLegends] = useState(null);
  const [summary, setSummary] = useState(null);
  const [markers, setMarkers] = useState(null);
  const dispatch = useDispatch();
  const countryDetails = useSelector(getCbCountryDetails);
  const clusterDetails = useSelector(getCbClusterDetails);
  const areaDetails = useSelector(getCbAreaDetails);
  const thanaDetails = useSelector(getCbThanaDetails);
  const selectedState = useSelector(getSelectedState);
  const filteringState = useSelector(getFilteringState);
  const filterType = useSelector(getFilterType);
  const filteringDateRange = useSelector(getFilteringDateRange);
  const ckuMapData = useSelector(getCkuMapData);

  const getSelectedStateId = () => {
    let id = null;
    switch (selectedState) {
      case COUNTRY:
        id = countryDetails.id;
        break;
      case CLUSTER:
        id = clusterDetails.id;
        break;
      case AREA:
        id = areaDetails.id;
        break;
      case THANA:
        id = thanaDetails.id;
        break;
      default:
        id = null;
    }
    return id;
  };

  const onClickRegion = event => {
    const id = event.target.feature.properties.id;
    dispatch(forward_fetch_by_selected_state(id, selectedState));
  };

  const onRightClick = event => {
    dispatch(backward_fetch_by_selected_state(selectedState));
  };

  useEffect(() => {
    if (!countryDetails.map) return;
    dispatch(fetch_cb_country_map_by_id(countryDetails.map));
  }, [countryDetails]);

  useEffect(() => {
    if (!clusterDetails.map) return;
    dispatch(fetch_cb_cluster_map_by_id(clusterDetails.map));
  }, [clusterDetails]);

  useEffect(() => {
    if (!areaDetails.map) return;
    dispatch(fetch_cb_area_map_by_id(areaDetails.map));
  }, [areaDetails]);

  useEffect(() => {
    if (!thanaDetails.map) return;
    dispatch(fetch_cb_thana_map_by_id(thanaDetails.map));
  }, [thanaDetails]);

  useEffect(() => {
    //Doing api calls here
    if (!selectedState) return;
    const id = getSelectedStateId();
    const date = filteringDateRange.format('YYYY-MM-DD');
    if (filteringState.partner_update)
      dispatch(fetch_partners(selectedState, id));
    else if (filteringState.partner_transaction)
      dispatch(
        fetch_partners_with_transaction(selectedState, id, filterType, date)
      );
    if (filteringState.partner_update && selectedState !== THANA)
      dispatch(fetch_partner_update_data(selectedState, id, filterType, date));
    if (filteringState.partner_transaction && selectedState !== THANA)
      dispatch(
        fetch_partner_transaction_summary_data(
          selectedState,
          id,
          filterType,
          date
        )
      );
  }, [selectedState, filteringState, filterType, filteringDateRange]);

  useEffect(() => {
    // Doing map data population on country, cluster, area state
    if (selectedState === THANA) return;

    let regional_info = [];
    let key = null;

    switch (selectedState) {
      case COUNTRY:
        regional_info = countryDetails.cb_clusters;
        key = 'cb_clusters';
        break;
      case CLUSTER:
        regional_info = clusterDetails.cb_areas;
        key = 'cb_areas';
        break;
      case AREA:
        regional_info = areaDetails.cb_thanas;
        key = 'cb_thanas';
        break;
      default:
        break;
    }

    let map_data = ckuMapData.data[key];
    let partners_update = ckuMapData.partnersUpdate[key];
    let partner_transactions_summary =
      ckuMapData.partnerTransactionsSummary[key];

    const merge_info = merge_regional_data(
      { regional_info, partners_update, partner_transactions_summary },
      filteringState
    );

    if (!merge_info) return;

    setChoropleth(make_choropleth(map_data, merge_info, filteringState));
    setLabel(make_label_by_state(map_data, merge_info, filteringState));
    setLegends(make_legends_by_state(filteringState));
    setSummary(make_summary_by_state(merge_info, filteringState));
    setBoundary(null);
    setMarkers(null);
  }, [ckuMapData]);

  useEffect(() => {
    // Doing map data population on thana state
    if (selectedState !== THANA || !ckuMapData.data.geo_json) return;
    const boundary_map = ckuMapData.data;
    const merge_markers_info = merge_regional_marker_data(ckuMapData);
    setBoundary(make_boundary(boundary_map));
    setChoropleth(null);
    setLabel(null);
    setLegends(null);
    setSummary(null);
    setMarkers(make_markers_by_state(merge_markers_info, filteringState));
  }, [ckuMapData]);

  return (
    <Map
      choropleth={choropleth}
      boundary={boundary}
      regionLabel={label}
      regionAction={onClickRegion}
      rightClickEvent={onRightClick}
      legends={legends}
      summary={summary}
      markers={markers}
    />
  );
}
